import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { PublicBannerCollectionItemResponse } from '../responses/public-banner-collection-item.response';
import { blank, Guid, Nillable, valueOrDefault } from '@web/utils';
import { BannerLocationType } from '../../../shared/enums/banner-location-type';

@Injectable({
  providedIn: 'root',
})
export class PublicBannerApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    type: BannerLocationType;
    request: CollectionRequest;
    cacheable?: Nillable<boolean>;
  }): Observable<CollectionResponse<PublicBannerCollectionItemResponse>> {
    const { type, request, cacheable } = options;
    const path = `banner/${type}`;
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByAuthTokenInterceptor()
      .withOptionalCacheInterceptor(() => valueOrDefault(cacheable, false))
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBannerCollectionItemResponse,
          ),
        ),
      );
  }

  public registerRedirect(options: { id: Guid }): Observable<void> {
    const { id } = options;
    const path = `banner/${HttpParams.Id}/redirect-register`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.post(url, null, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
